import type { Permission } from '@zenarty/shared';
import {
  Boxes,
  Building2,
  ClipboardCheck,
  Forklift,
  KanbanSquare,
  ListChecks,
  ListOrdered,
  PackageCheck,
  RefreshCcw,
  ShoppingCart,
  Truck,
  Waves,
  Handshake,
  History,
  LayoutDashboard,
  MapPin,
  MonitorSmartphone,
  Package,
  ScanBarcode,
  SearchCode,
  ShieldAlert,
  SlidersHorizontal,
  Store,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Shown when the user holds this permission (omit = everyone). */
  permission?: Permission;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/** Navigation grows with each milestone: add items here and a route in App.tsx. */
export const NAV: NavSection[] = [
  { title: 'Overview', items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }] },
  {
    title: 'Inbound',
    items: [
      { to: '/inbound/receipts', label: 'Receipts', icon: Truck, permission: 'inbound:read' },
      { to: '/inbound/putaway', label: 'Putaway', icon: Forklift, permission: 'putaway:execute' },
    ],
  },
  {
    title: 'Outbound',
    items: [
      {
        to: '/outbound/board',
        label: 'Order board',
        icon: KanbanSquare,
        permission: 'outbound:manage',
      },
      { to: '/outbound/orders', label: 'Orders', icon: ShoppingCart, permission: 'outbound:read' },
      { to: '/outbound/waves', label: 'Waves', icon: Waves, permission: 'outbound:manage' },
      { to: '/outbound/picking', label: 'Picking', icon: ListChecks, permission: 'outbound:pick' },
      {
        to: '/outbound/packing',
        label: 'Packing',
        icon: PackageCheck,
        permission: 'outbound:pack',
      },
      {
        to: '/outbound/replenishment',
        label: 'Replenishment',
        icon: RefreshCcw,
        permission: 'outbound:pick',
      },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { to: '/inventory', label: 'Stock', icon: Boxes, permission: 'inventory:read' },
      {
        to: '/inventory/counts',
        label: 'Cycle counts',
        icon: ClipboardCheck,
        permission: 'inventory:count',
      },
      {
        to: '/inventory/holds',
        label: 'Holds & recalls',
        icon: ShieldAlert,
        permission: 'inventory:hold',
      },
      {
        to: '/inventory/trace',
        label: 'Lot trace',
        icon: SearchCode,
        permission: 'inventory:read',
      },
    ],
  },
  {
    title: 'Catalog',
    items: [
      { to: '/items', label: 'Items', icon: Package, permission: 'item:read' },
      { to: '/locations', label: 'Locations', icon: MapPin, permission: 'location:read' },
      { to: '/partners', label: 'Trading partners', icon: Handshake, permission: 'partner:read' },
    ],
  },
  {
    title: 'Setup',
    items: [
      { to: '/sites', label: 'Sites', icon: Building2, permission: 'site:read' },
      { to: '/clients', label: 'Clients', icon: Store, permission: 'client:read' },
      { to: '/users', label: 'Users & access', icon: Users, permission: 'user:read' },
      { to: '/devices', label: 'Scanners', icon: MonitorSmartphone, permission: 'device:read' },
      {
        to: '/putaway-rules',
        label: 'Putaway rules',
        icon: ListOrdered,
        permission: 'config:read',
      },
      {
        to: '/configuration',
        label: 'Configuration',
        icon: SlidersHorizontal,
        permission: 'config:read',
      },
    ],
  },
  {
    title: 'Governance',
    items: [
      { to: '/audit', label: 'Audit log', icon: History, permission: 'audit:read' },
      { to: '/tools/barcode', label: 'Barcode decoder', icon: ScanBarcode },
    ],
  },
];

export function findNav(path: string): { section: string; item: NavItem } | null {
  // Longest matching entry wins, so /inventory/counts maps to "Cycle counts", not "Stock".
  let best: { section: string; item: NavItem } | null = null;
  for (const s of NAV) {
    for (const item of s.items) {
      const hit =
        item.to === '/' ? path === '/' : path === item.to || path.startsWith(`${item.to}/`);
      if (hit && (!best || item.to.length > best.item.to.length)) best = { section: s.title, item };
    }
  }
  return best;
}
